import React, { useRef, useEffect, useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { api, type Vivienda } from '@/lib/api';

declare const maplibregl: typeof import('maplibre-gl');

const ESTADOS = [
  { value: 'construccion', label: 'En construcción', icon: '🏗️', color: '#f59e0b' },
  { value: 'lista', label: 'Lista', icon: '🔑', color: '#3b82f6' },
  { value: 'entregada', label: 'Entregada', icon: '🏠', color: '#22c55e' },
] as const;

type EstadoType = 'construccion' | 'lista' | 'entregada';

interface FormData {
  // Step 1
  proyecto: string;
  direccion: string;
  bloque: string;
  piso: string;
  apartamento: string;
  estado: EstadoType;
  // Step 2
  benefNombre: string;
  benefCedula: string;
  benefTelefono: string;
  benefFamilias: string;
  fechaEntrega: string;
  // Step 3
  lat: number | null;
  lng: number | null;
}

const INITIAL: FormData = {
  proyecto: '', direccion: '', bloque: '', piso: '', apartamento: '', estado: 'construccion',
  benefNombre: '', benefCedula: '', benefTelefono: '', benefFamilias: '', fechaEntrega: '',
  lat: null, lng: null,
};

export default function ViviendaSistView({ onClose }: { onClose?: () => void } = {}) {
  const { token } = useAuthStore();
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>(INITIAL);
  const [success, setSuccess] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const markerRef = useRef<any>(null);

  const { data: viviendas = [] } = useQuery<Vivienda[]>({
    queryKey: ['viviendas'],
    queryFn: () => api.viviendas(token!),
    enabled: !!token,
  });

  const proyectosExistentes = [...new Set(viviendas.map(v => v.proyecto))];

  const crearMut = useMutation({
    mutationFn: (body: Partial<Vivienda>) => api.crearVivienda(token!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['viviendas'] });
      setSuccess(true);
      setTimeout(() => { setSuccess(false); setStep(1); setForm(INITIAL); onClose?.(); }, 2200);
    },
  });

  function set(key: keyof FormData, val: string | number | null) {
    setForm(f => ({ ...f, [key]: val }));
  }

  // Map for step 3
  useEffect(() => {
    if (step !== 3 || !mapRef.current || mapInstance.current) return;
    mapInstance.current = new maplibregl.Map({
      container: mapRef.current!,
      style: 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
      center: [-66.9146, 10.4806],
      zoom: 14,
    });
    mapInstance.current.on('click', (e: any) => {
      const { lng, lat } = e.lngLat;
      set('lat', lat);
      set('lng', lng);
      if (markerRef.current) markerRef.current.remove();
      const el = document.createElement('div');
      el.style.cssText = 'width:22px;height:22px;border-radius:50%;background:#60a5fa;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.5)';
      markerRef.current = new maplibregl.Marker({ element: el })
        .setLngLat([lng, lat])
        .addTo(mapInstance.current!);
    });
    return () => { mapInstance.current?.remove(); mapInstance.current = null; };
  }, [step]);

  function handleSubmit() {
    const body: Partial<Vivienda> = {
      proyecto: form.proyecto,
      direccion: form.direccion,
      estado: form.estado,
      ...(form.bloque && { bloque: form.bloque }),
      ...(form.piso && { piso: form.piso }),
      ...(form.apartamento && { apartamento: form.apartamento }),
      ...(form.benefNombre && { benefNombre: form.benefNombre }),
      ...(form.benefCedula && { benefCedula: form.benefCedula }),
      ...(form.benefTelefono && { benefTelefono: form.benefTelefono }),
      ...(form.benefFamilias && { benefFamilias: Number(form.benefFamilias) }),
      ...(form.fechaEntrega && { fechaEntrega: form.fechaEntrega }),
      ...(form.lat != null && { lat: form.lat }),
      ...(form.lng != null && { lng: form.lng }),
    };
    crearMut.mutate(body);
  }

  if (success) {
    return (
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0a1628', gap: 16 }}>
        <div style={{ fontSize: 56 }}>✅</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#22c55e' }}>Vivienda registrada</div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>Preparando nuevo formulario…</div>
      </div>
    );
  }

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#0a1628', overflowY: 'auto' }}>
      <div style={{ width: '100%', maxWidth: 560, padding: '28px 20px 40px' }}>
        {/* Header */}
        <div style={{ marginBottom: 28, textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#60a5fa' }}>🏠 Registro de Vivienda</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>Paso {step} de 3</div>
          {/* Progress */}
          <div style={{ display: 'flex', gap: 6, marginTop: 14, justifyContent: 'center' }}>
            {[1, 2, 3].map(s => (
              <div key={s} style={{ height: 4, width: 60, borderRadius: 2, background: s <= step ? '#60a5fa' : 'rgba(255,255,255,0.1)', transition: 'background 0.3s' }} />
            ))}
          </div>
        </div>

        {/* Step 1: Project info */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <SectionTitle>Información del proyecto</SectionTitle>

            <div>
              <FieldLabel>Proyecto *</FieldLabel>
              <input value={form.proyecto} onChange={e => set('proyecto', e.target.value)}
                list="proyectos-list"
                placeholder="Nombre del proyecto"
                style={inputStyle} />
              <datalist id="proyectos-list">
                {proyectosExistentes.map(p => <option key={p} value={p} />)}
              </datalist>
            </div>

            <div>
              <FieldLabel>Dirección *</FieldLabel>
              <input value={form.direccion} onChange={e => set('direccion', e.target.value)}
                placeholder="Dirección completa"
                style={inputStyle} />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <FieldLabel>Bloque</FieldLabel>
                <input value={form.bloque} onChange={e => set('bloque', e.target.value)}
                  placeholder="B1" style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <FieldLabel>Piso</FieldLabel>
                <input value={form.piso} onChange={e => set('piso', e.target.value)}
                  placeholder="P3" style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <FieldLabel>Apartamento</FieldLabel>
                <input value={form.apartamento} onChange={e => set('apartamento', e.target.value)}
                  placeholder="3A" style={inputStyle} />
              </div>
            </div>

            <div>
              <FieldLabel>Estado de la vivienda *</FieldLabel>
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                {ESTADOS.map(e => (
                  <button key={e.value} onClick={() => set('estado', e.value as EstadoType)}
                    style={{ flex: 1, padding: '10px 6px', borderRadius: 10, border: `1.5px solid ${e.color}`, background: form.estado === e.value ? e.color : 'transparent', color: form.estado === e.value ? '#fff' : e.color, cursor: 'pointer', fontSize: 12, fontWeight: 700, transition: 'all 0.15s' }}>
                    {e.icon}<br />{e.label}
                  </button>
                ))}
              </div>
            </div>

            <NavButtons onNext={() => setStep(2)} nextDisabled={!form.proyecto || !form.direccion} />
          </div>
        )}

        {/* Step 2: Beneficiary */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <SectionTitle>Datos del beneficiario</SectionTitle>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: -8 }}>Opcional — solo visible para admin</div>

            <div>
              <FieldLabel>Nombre completo</FieldLabel>
              <input value={form.benefNombre} onChange={e => set('benefNombre', e.target.value)}
                placeholder="Juan Pérez" style={inputStyle} />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <FieldLabel>Cédula</FieldLabel>
                <input value={form.benefCedula} onChange={e => set('benefCedula', e.target.value)}
                  placeholder="V-12.345.678" style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <FieldLabel>Teléfono</FieldLabel>
                <input value={form.benefTelefono} onChange={e => set('benefTelefono', e.target.value)}
                  placeholder="0414-1234567" style={inputStyle} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <FieldLabel>N° de familias</FieldLabel>
                <input type="number" min={1} value={form.benefFamilias} onChange={e => set('benefFamilias', e.target.value)}
                  placeholder="1" style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <FieldLabel>Fecha de entrega</FieldLabel>
                <input type="date" value={form.fechaEntrega} onChange={e => set('fechaEntrega', e.target.value)}
                  style={{ ...inputStyle, colorScheme: 'dark' }} />
              </div>
            </div>

            <NavButtons onBack={() => setStep(1)} onNext={() => setStep(3)} />
          </div>
        )}

        {/* Step 3: Map location */}
        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <SectionTitle>Ubicación georeferencial</SectionTitle>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: -8 }}>Toca el mapa para marcar la ubicación (opcional)</div>

            <div ref={mapRef} style={{ height: 300, borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }} />

            {form.lat != null && form.lng != null && (
              <div style={{ fontSize: 11, color: '#60a5fa', background: 'rgba(96,165,250,0.08)', borderRadius: 8, padding: '7px 12px' }}>
                📍 {form.lat.toFixed(6)}, {form.lng.toFixed(6)}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button onClick={() => setStep(2)}
                style={{ flex: 1, padding: '11px 0', borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                ← Atrás
              </button>
              {form.lat != null && form.lng != null && (
                <button onClick={() => { set('lat', null); set('lng', null); if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; } }}
                  style={{ padding: '11px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>
                  Quitar
                </button>
              )}
              <button onClick={handleSubmit} disabled={crearMut.isPending}
                style={{ flex: 2, padding: '11px 0', borderRadius: 10, background: crearMut.isPending ? 'rgba(96,165,250,0.3)' : '#1d4ed8', border: 'none', color: '#fff', cursor: crearMut.isPending ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700 }}>
                {crearMut.isPending ? 'Guardando…' : '✅ Guardar vivienda'}
              </button>
            </div>

            {crearMut.isError && (
              <div style={{ fontSize: 12, color: '#ef4444', background: 'rgba(239,68,68,0.08)', borderRadius: 8, padding: '8px 12px' }}>
                Error al guardar. Intenta de nuevo.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 9, border: '1px solid rgba(255,255,255,0.12)',
  background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 14, fontWeight: 800, color: '#60a5fa', marginBottom: 2 }}>{children}</div>;
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 10.5, fontWeight: 700, color: 'rgba(255,255,255,0.4)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</div>;
}

function NavButtons({ onBack, onNext, nextDisabled }: { onBack?: () => void; onNext?: () => void; nextDisabled?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
      {onBack && (
        <button onClick={onBack}
          style={{ flex: 1, padding: '11px 0', borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          ← Atrás
        </button>
      )}
      {onNext && (
        <button onClick={onNext} disabled={nextDisabled}
          style={{ flex: 2, padding: '11px 0', borderRadius: 10, background: nextDisabled ? 'rgba(96,165,250,0.15)' : '#1d4ed8', border: 'none', color: nextDisabled ? 'rgba(255,255,255,0.3)' : '#fff', cursor: nextDisabled ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700 }}>
          Siguiente →
        </button>
      )}
    </div>
  );
}
